import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { PlayerProvider } from "./contexts/PlayerContext";
import { FavoritesProvider } from "./contexts/FavoritesContext";
import { PlaylistsProvider } from "./contexts/PlaylistsContext";

import Header from "./components/Header";
import Footer from "./components/Footer";
import AudioPlayer from "./components/AudioPlayer";
import ProtectedRoute from "./components/ProtectedRoute";

// Eagerly load Landing (above the fold for first paint) + auth pages used a lot.
import Landing from "./pages/Landing";
import PwaInstallBanner from "./components/PwaInstallBanner";
import PromoStrip from "./components/PromoStrip";
import AnnouncementTicker from "./components/AnnouncementTicker";
import AomSponsorStrip from "./components/AomSponsorStrip";
import VisitTracker from "./components/VisitTracker";
import PushPrompt from "./components/PushPrompt";
import CookieConsent from "./components/CookieConsent";
import GoogleAdSense from "./components/GoogleAdSense";
import MetaPixel from "./components/MetaPixel";
import NavArrows from "./components/NavArrows";
import MonetagLoader from "./components/MonetagLoader";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PhotoGate from "./components/PhotoGate";

// Lazy-load everything else for smaller initial bundle.
const Catalog = lazy(() => import("./pages/Catalog"));
const Playlist = lazy(() => import("./pages/Playlist"));
const MyPlaylists = lazy(() => import("./pages/MyPlaylists"));
const PlaylistView = lazy(() => import("./pages/PlaylistView"));
const ArtistPrices = lazy(() => import("./pages/ArtistPrices"));
const Interview = lazy(() => import("./pages/Interview"));
const TrackDetail = lazy(() => import("./pages/TrackDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const Account = lazy(() => import("./pages/Account"));
const Artist = lazy(() => import("./pages/Artist"));
const ArtistUpload = lazy(() => import("./pages/ArtistUpload"));
const EditTrack = lazy(() => import("./pages/EditTrack"));
const Admin = lazy(() => import("./pages/Admin"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const Legal = lazy(() => import("./pages/Legal"));
const Commander = lazy(() => import("./pages/Commander"));
const Ghostwriting = lazy(() => import("./pages/Ghostwriting"));
const B2B = lazy(() => import("./pages/B2B"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Talent = lazy(() => import("./pages/Talent"));
const TalentConcept = lazy(() => import("./pages/TalentConcept"));
const TalentCandidate = lazy(() => import("./pages/TalentCandidate"));
const TalentVotes = lazy(() => import("./pages/TalentVotes"));
const ArtistOfMonth = lazy(() => import("./pages/ArtistOfMonth"));
const AomReglement = lazy(() => import("./pages/AomReglement"));
const PostDetail = lazy(() => import("./pages/PostDetail"));
const Favorites = lazy(() => import("./pages/Favorites"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const CheckEmail = lazy(() => import("./pages/CheckEmail"));
const BecomeArtist = lazy(() => import("./pages/BecomeArtist"));
const ProducerProfile = lazy(() => import("./pages/ProducerProfile"));
const Producers = lazy(() => import("./pages/Producers"));
const ProfileEditor = lazy(() => import("./pages/ProfileEditor"));
const MyNews = lazy(() => import("./pages/MyNews"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const MyOffers = lazy(() => import("./pages/MyOffers"));
const Opportunities = lazy(() => import("./pages/Opportunities"));
const AiStudio = lazy(() => import("./pages/AiStudio"));
const MyGifts = lazy(() => import("./pages/MyGifts"));
const MyServiceOrders = lazy(() => import("./pages/MyServiceOrders"));
const ArtistProjects = lazy(() => import("./pages/ArtistProjects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const ArtistVideos = lazy(() => import("./pages/ArtistVideos"));
const MyStreamEarnings = lazy(() => import("./pages/MyStreamEarnings"));
const Messages = lazy(() => import("./pages/Messages"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Feed = lazy(() => import("./pages/Feed"));
const ExternalGift = lazy(() => import("./pages/ExternalGift"));
const CreditsSuccess = lazy(() => import("./pages/CreditsSuccess"));
const CreditsPage = lazy(() => import("./pages/CreditsPage"));
const MboaBeat = lazy(() => import("./pages/MboaBeat"));
const BeatDetail = lazy(() => import("./pages/BeatDetail"));
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));

function PageFallback() {
    return (
        <div className="min-h-[40vh] flex items-center justify-center" data-testid="route-fallback">
            <div className="w-8 h-8 border-2 border-[#00A86B] border-t-transparent rounded-full animate-spin" />
        </div>
    );
}

function Layout({ children }) {
    return (
        <div className="App min-h-screen flex flex-col bg-[#0F0F0F] text-[#F4F4F4]">
            <VisitTracker />
            <Header />
            <main className="pt-16 flex-1 pb-24">
                <AnnouncementTicker />
                <AomSponsorStrip />
                <PromoStrip />
                {children}
            </main>
            <Footer />
            <AudioPlayer />
            <NavArrows />
            <PwaInstallBanner />
            <PushPrompt />
            <CookieConsent />
            <GoogleAdSense />
            <MetaPixel />
            <MonetagLoader />
            <PhotoGate />
        </div>
    );
}

function App() {
    // Capture globale du code de parrainage/affiliation (?ref=) dès l'arrivée,
    // même si l'inscription se fait plus tard depuis une autre page.
    const _ref = new URLSearchParams(window.location.search).get("ref");
    if (_ref) localStorage.setItem("mboa_ref", _ref);
    return (
        <AuthProvider>
            <CartProvider>
                <PlayerProvider>
                    <FavoritesProvider>
                        <BrowserRouter>
                          <PlaylistsProvider>
                            <Layout>
                                <Suspense fallback={<PageFallback />}>
                                    <Routes>
                                        <Route path="/" element={<Landing />} />
                                        <Route path="/catalog" element={<Catalog />} />
                                        <Route path="/playlist" element={<Playlist />} />
                                        <Route path="/playlists/:slug" element={<PlaylistView />} />
                                        <Route path="/me/playlists" element={<ProtectedRoute><MyPlaylists /></ProtectedRoute>} />
                                        <Route path="/beats" element={<MboaBeat />} />
                                        <Route path="/beats/:slug" element={<BeatDetail />} />
                                        <Route path="/services" element={<Services />} />
                                        <Route path="/services/:slug" element={<ServiceDetail />} />
                                        <Route path="/track/:id" element={<TrackDetail />} />
                                        <Route path="/projets/:slug" element={<ProjectDetail />} />
                                        <Route path="/post/:slug" element={<PostDetail />} />
                                        <Route path="/commander" element={<Commander />} />
                                        <Route path="/ghostwriting" element={<Ghostwriting />} />
                                        <Route path="/b2b" element={<B2B />} />
                                        <Route path="/about" element={<About />} />
                                        <Route path="/blog" element={<Blog />} />
                                        <Route path="/blog/:slug" element={<BlogPost />} />
                                        <Route path="/talent" element={<Talent />} />
                                        <Route path="/talent/concept" element={<TalentConcept />} />
                                        <Route path="/talent/c/:key" element={<TalentCandidate />} />
                                        <Route path="/talent/votes" element={<TalentVotes />} />
                                        <Route path="/artiste-du-mois" element={<ArtistOfMonth />} />
                                        <Route path="/artiste-du-mois/reglement" element={<AomReglement />} />
                                        <Route path="/cart" element={<Cart />} />
                                        <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                                        <Route path="/order/:id" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
                                        <Route path="/login" element={<Login />} />
                                        <Route path="/register" element={<Register />} />
                                        <Route path="/forgot-password" element={<ForgotPassword />} />
                                        <Route path="/reset-password" element={<ResetPassword />} />
                                        <Route path="/verify-email" element={<VerifyEmail />} />
                                        <Route path="/check-email" element={<CheckEmail />} />
                                        <Route path="/producer/:username" element={<ProducerProfile />} />
                                        <Route path="/artistes/:username" element={<ProducerProfile />} />
                                        <Route path="/producers" element={<Producers />} />
                                        <Route path="/me/profile" element={<ProtectedRoute><ProfileEditor /></ProtectedRoute>} />
                                        <Route path="/me/news" element={<ProtectedRoute><MyNews /></ProtectedRoute>} />
                                        <Route path="/me/bookings" element={<ProtectedRoute><MyBookings /></ProtectedRoute>} />
                                        <Route path="/me/offers" element={<ProtectedRoute><MyOffers /></ProtectedRoute>} />
                                        <Route path="/opportunites" element={<Opportunities />} />
                                        <Route path="/ai-studio" element={<ProtectedRoute roles={["artist", "admin", "provider", "organizer", "media"]}><AiStudio /></ProtectedRoute>} />
                                        <Route path="/me/gifts" element={<ProtectedRoute roles={["artist", "admin", "provider", "organizer", "media"]}><MyGifts /></ProtectedRoute>} />
                                        <Route path="/me/stream-earnings" element={<ProtectedRoute roles={["artist", "admin"]}><MyStreamEarnings /></ProtectedRoute>} />
                                        <Route path="/me/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                                        <Route path="/me/services" element={<ProtectedRoute><MyServiceOrders /></ProtectedRoute>} />
                                        <Route path="/privacy" element={<Privacy />} />
                                        <Route path="/terms" element={<Terms />} />
                                        <Route path="/feed" element={<Feed />} />
                                        <Route path="/gift/:username" element={<ExternalGift />} />
                                        <Route path="/credits/success" element={<CreditsSuccess />} />
                                        <Route path="/me/credits" element={<CreditsPage />} />
                                        <Route path="/become-artist" element={<ProtectedRoute><BecomeArtist /></ProtectedRoute>} />
                                        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                                        <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
                                        <Route path="/artist" element={<ProtectedRoute roles={["artist", "admin"]}><Artist /></ProtectedRoute>} />
                                        <Route path="/artist/upload" element={<ProtectedRoute roles={["artist", "admin"]}><ArtistUpload /></ProtectedRoute>} />
                                        <Route path="/artist/projects" element={<ProtectedRoute roles={["artist", "admin"]}><ArtistProjects /></ProtectedRoute>} />
                                        <Route path="/artist/tarifs" element={<ProtectedRoute roles={["artist", "admin"]}><ArtistPrices /></ProtectedRoute>} />
                                        <Route path="/entrevue" element={<Interview />} />
                                        <Route path="/artist/videos" element={<ProtectedRoute roles={["artist", "admin"]}><ArtistVideos /></ProtectedRoute>} />
                                        <Route path="/artist/tracks/:id/edit" element={<ProtectedRoute roles={["artist", "admin"]}><EditTrack /></ProtectedRoute>} />
                                        <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><Admin /></ProtectedRoute>} />
                                        <Route path="/faq" element={<FAQ />} />
                                        <Route path="/contact" element={<Contact />} />
                                        <Route path="/legal" element={<Legal />} />
                                    </Routes>
                                </Suspense>
                            </Layout>
                            <Toaster theme="dark" position="top-right" richColors />
                          </PlaylistsProvider>
                        </BrowserRouter>
                    </FavoritesProvider>
                </PlayerProvider>
            </CartProvider>
        </AuthProvider>
    );
}

export default App;
